import { useState } from "react";

export interface ImageFallback {
  /** `false` cuando no hay dirección o cuando la que hay no cargó. */
  showImage: boolean;
  /** Va en el `onError` del `<img>`. */
  onError: () => void;
}

/**
 * Decide si una imagen de producto se puede pintar.
 *
 * ── Por qué esto es un hook y no vive dentro de la tarjeta ───────────
 * Las imágenes del catálogo se guardan por URL, así que apuntan a un
 * servidor que no controlamos: se cae, cambia la ruta, o alguien escribió
 * mal la dirección. Sin capturar el fallo, el navegador pinta su icono de
 * imagen rota con el texto alternativo encima — se ve averiado y además
 * ilegible.
 *
 * Esa regla la estrenó `ProductCardMedia`, pero la necesitan igual la
 * galería de la ficha y la miniatura del carrito. Copiarla tres veces
 * significaría que el día que cambie el marcador cambie en una sola.
 *
 * ── El detalle que se olvida ─────────────────────────────────────────
 * `src` es la clave del estado: si la dirección cambia —al pasar de una
 * miniatura a otra en la galería— se vuelve a intentar en vez de quedarse
 * marcada como rota para siempre por culpa de la imagen anterior.
 */
export function useImageFallback(src: string | undefined): ImageFallback {
  const [failed, setFailed] = useState(false);
  const [triedSrc, setTriedSrc] = useState(src);

  if (triedSrc !== src) {
    setTriedSrc(src);
    setFailed(false);
  }

  return {
    showImage: Boolean(src) && !failed,
    onError: () => setFailed(true),
  };
}
